import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import posthog from 'posthog-js';
import { PostHogProvider } from '@posthog/react';
import './index.css';
import i18n from './i18n';
import App from './App';
import { initSentry } from './lib/sentry';

// Defer Sentry initialization to after first paint
// This improves initial load performance
if (typeof requestIdleCallback !== 'undefined') {
  requestIdleCallback(
    () => {
      initSentry();
    },
    { timeout: 2000 }
  );
} else {
  // Fallback for Safari and browsers without requestIdleCallback
  setTimeout(() => {
    initSentry();
  }, 50);
}

// Initialize PostHog after initial render to not block first paint
// Using requestIdleCallback for better performance
const initPostHog = () => {
  if (process.env.REACT_APP_POSTHOG_KEY && !posthog.__loaded) {
    posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
      api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com',
      defaults: '2025-11-30',
      capture_exceptions: true,
      debug: process.env.NODE_ENV === 'development',
      loaded: (posthog) => {
        // PostHog is now ready
        if (process.env.NODE_ENV === 'development') {
          console.log('PostHog loaded');
        }
      },
    });
  }
};

// Defer PostHog initialization to after first paint
if (typeof requestIdleCallback !== 'undefined') {
  requestIdleCallback(initPostHog, { timeout: 3000 });
} else {
  // Fallback for Safari
  setTimeout(initPostHog, 100);
}

const rootElement = document.getElementById('root');
const root = createRoot(rootElement);
root.render(
  <StrictMode>
    <PostHogProvider client={posthog}>
      <I18nextProvider i18n={i18n}>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <App />
          <Analytics />
          <SpeedInsights />
        </BrowserRouter>
      </I18nextProvider>
    </PostHogProvider>
  </StrictMode>
);
